import React, { createContext, useContext, useMemo } from 'react';
import { notification } from 'antd';
import { NotificationPlacement } from 'antd/es/notification/interface';

type SnackbarType = 'info' | 'success' | 'warning' | 'error';

type SnackbarApi = {
  open: (opts: {
    message?: React.ReactNode;
    description?: React.ReactNode;
    type?: SnackbarType;
    duration?: number;
    placement?: NotificationPlacement;
    key?: string;
  }) => void;
  success: (
    message: React.ReactNode,
    description?: React.ReactNode,
    opts?: { duration?: number; key?: string },
  ) => void;
  error: (
    message: React.ReactNode,
    description?: React.ReactNode,
    opts?: { duration?: number; key?: string },
  ) => void;
  info: (
    message: React.ReactNode,
    description?: React.ReactNode,
    opts?: { duration?: number; key?: string },
  ) => void;
  warning: (
    message: React.ReactNode,
    description?: React.ReactNode,
    opts?: { duration?: number; key?: string },
  ) => void;
  close: (key: string) => void;
  destroy: () => void;
};

const SnackbarContext = createContext<SnackbarApi | null>(null);

const SnackbarProvider: React.FC<{
  children: React.ReactNode;
  placement?: NotificationPlacement;
  defaultDuration?: number;
}> = ({ children, placement = 'bottomLeft', defaultDuration = 3 }) => {
  const [api, contextHolder] = notification.useNotification({
    placement,
    maxCount: 3,
    duration: defaultDuration,
  });

  const snackbarApi = useMemo<SnackbarApi>(() => {
    const open = (opts: {
      message?: React.ReactNode;
      description?: React.ReactNode;
      type?: SnackbarType;
      duration?: number;
      placement?: NotificationPlacement;
      key?: string;
    }) => {
      const {
        message,
        description,
        type = 'info',
        duration,
        placement: optPlacement,
        key,
      } = opts;
      const fn = api[type] ?? api.open;
      fn({
        message,
        description,
        duration,
        key,
        placement: optPlacement ?? placement,
      });
    };

    return {
      open,
      success: (m, d, o) =>
        open({ message: m, description: d, type: 'success', ...o }),
      error: (m, d, o) =>
        open({ message: m, description: d, type: 'error', ...o }),
      info: (m, d, o) =>
        open({ message: m, description: d, type: 'info', ...o }),
      warning: (m, d, o) =>
        open({ message: m, description: d, type: 'warning', ...o }),
      close: (key) => notification.destroy(key),
      destroy: () => notification.destroy(),
    };
  }, []);

  return (
    <SnackbarContext.Provider value={snackbarApi}>
      {contextHolder}
      {children}
    </SnackbarContext.Provider>
  );
};

const useSnackbar = (): SnackbarApi => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within SnackbarProvider');
  return ctx;
};

export { SnackbarProvider, useSnackbar };
